import { useState } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useUpdateComment, useDeleteComment } from '@/hooks/useComments'
import { useAuth } from '@/contexts/AuthContext'
import { Edit2, Trash2, Check, X } from 'lucide-react'
import { Textarea } from '@/components/ui/textarea'
import type { Comment } from '@/types/database'
import { formatDistanceToNow } from 'date-fns'
import { cn } from '@/lib/utils'

interface CommentItemProps {
  comment: Comment
  onMentionClick?: (userId: string) => void
}

export function CommentItem({ comment, onMentionClick }: CommentItemProps) {
  const { user } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState(comment.content)
  const updateComment = useUpdateComment(comment.id)
  const deleteComment = useDeleteComment()

  const isOwner = user?.id === comment.user_id
  const canEdit = isOwner && !comment.is_system

  const handleSaveEdit = async () => {
    if (!editContent.trim()) return

    await updateComment.mutateAsync({
      content: editContent.trim(),
    })
    setIsEditing(false)
  }

  const handleCancelEdit = () => {
    setEditContent(comment.content)
    setIsEditing(false)
  }

  const handleDelete = async () => {
    if (confirm('Are you sure you want to delete this comment?')) {
      await deleteComment.mutateAsync(comment.id)
    }
  }

  const renderContent = () => {
    if (isEditing) {
      return (
        <div className="space-y-2">
          <Textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="min-h-[80px]"
            placeholder="Write a comment..."
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleSaveEdit}
              disabled={!editContent.trim() || updateComment.isPending}
            >
              <Check className="h-3 w-3 mr-1" />
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancelEdit}>
              <X className="h-3 w-3 mr-1" />
              Cancel
            </Button>
          </div>
        </div>
      )
    }

    // Render markdown-style content with @mentions highlighted
    const parts = comment.content.split(/(@\[[^\]]+\]|@\w+)/g)
    return (
      <div className="text-sm whitespace-pre-wrap break-words">
        {parts.map((part, index) => {
          const mentionMatch = part.match(/^@\[([^\]]+)\]$/) || part.match(/^@(\w+)$/)
          if (mentionMatch) {
            return (
              <span
                key={index}
                className="text-blue-600 dark:text-blue-400 font-medium hover:underline cursor-pointer"
                onClick={() => onMentionClick?.(part)}
              >
                {part}
              </span>
            )
          }
          return <span key={index}>{part}</span>
        })}
      </div>
    )
  }

  const getUserInitials = () => {
    if (comment.is_system) return 'AT'
    const name = comment.user?.name || comment.user?.email || 'U'
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const wasEdited = comment.updated_at !== comment.created_at

  return (
    <div
      className={cn(
        'flex gap-3 p-4 rounded-lg',
        comment.is_system
          ? 'bg-muted/40 border border-muted'
          : 'bg-background hover:bg-muted/20 transition-colors'
      )}
    >
      {/* Avatar */}
      <Avatar className="h-8 w-8 flex-shrink-0">
        <AvatarImage src={comment.user?.avatar_url} alt={comment.user?.name} />
        <AvatarFallback
          className={cn(
            comment.is_system && 'bg-primary text-primary-foreground'
          )}
        >
          {getUserInitials()}
        </AvatarFallback>
      </Avatar>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="font-medium text-sm">
            {comment.is_system ? 'AionTrack' : comment.user?.name || 'Unknown User'}
          </span>

          {comment.is_system && (
            <Badge variant="secondary" className="text-xs">
              System
            </Badge>
          )}

          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
          </span>

          {wasEdited && !isEditing && (
            <span className="text-xs text-muted-foreground italic">(edited)</span>
          )}

          {/* Actions */}
          {canEdit && !isEditing && (
            <div className="ml-auto flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => setIsEditing(true)}
              >
                <Edit2 className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                onClick={handleDelete}
                disabled={deleteComment.isPending}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>

        {/* Comment content */}
        {renderContent()}
      </div>
    </div>
  )
}
